/**
 * The landing's scroll rail (#1719, round 8 — founder ask: "a little
 * interactive scroll bar on the side which will indicate to the user
 * which section of the page they are currently viewing").
 *
 * A fixed left-edge `<nav>` listing the landing's sections in the
 * page's own structural vocabulary — hairline ticks and mono
 * micro-labels, the `SectionRule` register, not a dot carousel. The
 * active section's tick lengthens and takes the accent; clicking a row
 * jumps to the section (smooth unless `prefers-reduced-motion`).
 *
 * Detection lives in the PAGE (`useActiveSection`, an IntersectionObserver
 * band across the viewport's middle); this component is purely
 * presentational so the same active-section state can also drive the
 * per-section tint shift without a second observer.
 *
 * Responsive discipline: desktop keeps the full vertical side rail.
 * Below `lg`, the same links become a compact horizontal rail fixed to
 * the bottom edge. It scrolls sideways when the section names exceed
 * the viewport instead of hiding the page's navigation entirely.
 *
 * The landing, The problem with rostering and How it works use this rail.
 * The product pages keep their sticky subnav.
 */
import type { LandingSection } from "../LandingPage";
import { withTariiWord } from "./TariiWord";

export function ScrollRail({
  sections,
  active,
  wordmark = false,
}: {
  sections: ReadonlyArray<LandingSection>;
  active: string;
  /** Show each "Tarii" in a section name as the wordmark image. */
  wordmark?: boolean;
}) {
  return (
    <nav
      aria-label="Page sections"
      className="fixed right-3 bottom-3 left-3 z-30 border border-border bg-bg px-2 lg:right-auto lg:bottom-auto lg:left-4 lg:top-1/2 lg:-translate-y-1/2 lg:border-0 lg:bg-transparent lg:px-0"
    >
      <ul className="la-scroll-rail flex overflow-x-auto lg:flex-col lg:overflow-visible">
        {sections.map((section) => {
          const isActive = section.id === active;
          return (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                aria-label={section.name}
                aria-current={isActive ? "true" : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  document.getElementById(section.id)?.scrollIntoView({
                    behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
                      ? "auto"
                      : "smooth",
                    block: "start",
                  });
                }}
                className="group flex shrink-0 items-center gap-2 min-h-11 py-2 pr-4 lg:gap-2.5 lg:pr-0"
              >
                <span aria-hidden="true" className="flex w-5 shrink-0 items-center">
                  <span
                    className={`h-px transition-all motion-reduce:transition-none ${
                      isActive
                        ? "w-5 bg-accent-fg"
                        : "w-3 bg-border-strong group-hover:bg-fg-subtle"
                    }`}
                  />
                </span>
                <span
                  className={`whitespace-nowrap text-[12px] lg:text-[12px] ${
                    isActive ? "text-accent-fg" : "text-fg-faint group-hover:text-fg-subtle"
                  }`}
                >
                  {wordmark ? withTariiWord(section.name) : section.name}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
